import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, Pencil } from "lucide-react";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ui } from "../../../components/ui";
import { MovementTypeBadge } from "./MovementTypeBadge";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

const iconBtnClass =
  "inline-grid place-items-center h-8 w-8 rounded-lg border border-primary/30 text-primary hover:bg-primary hover:text-white transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint shrink-0">{label}</span>
      <span className="font-semibold text-foreground text-right truncate">{value}</span>
    </div>
  );
}

function displayId(value) {
  return value == null || value === "" ? "—" : String(value);
}

function productSummary(items) {
  const names = (Array.isArray(items) ? items : [])
    .map((item) =>
      String(
        item?.product_name ||
          item?.productName ||
          item?.part_number ||
          item?.partNumber ||
          ""
      ).trim()
    )
    .filter(Boolean);
  if (names.length === 0) return "—";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function canDownload(row) {
  return row?.stockJournalId != null || row?.customerAllocationId != null;
}

export function DeliveryChallanTable({
  data,
  page,
  pageSize,
  onPageChange,
  totalItems,
  downloadingId = null,
  onEdit,
  onDownload,
}) {
  const { t } = useTranslation();

  const resolvedTotalItems = totalItems ?? data.length;
  const totalPages = Math.max(1, Math.ceil(resolvedTotalItems / pageSize));
  const pagedRows = useMemo(() => {
    if (totalItems != null) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, totalItems]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const match = String(isoStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dateObj = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(isoStr);
    if (!Number.isFinite(dateObj.getTime())) return "—";
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const ActionButtons = ({ row }) => {
    const editable = !row.invoiceNumber;
    const downloadable = canDownload(row);
    const downloading = downloadingId === row.deliveryChallanId;
    return (
      <div
        className="inline-flex items-center justify-end gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        {editable ? (
          <button
            type="button"
            title={t("common.edit")}
            aria-label={t("common.edit")}
            onClick={() => onEdit?.(row)}
            className={iconBtnClass}
          >
            <Pencil className="h-4 w-4" />
          </button>
        ) : (
          <span className="h-8 w-8" aria-hidden="true" />
        )}
        <button
          type="button"
          title={
            downloadable
              ? t("inventory.downloadChallan")
              : t("inventory.challanDownloadUnavailable")
          }
          aria-label={t("inventory.downloadChallan")}
          disabled={!downloadable || downloading}
          onClick={() => onDownload?.(row)}
          className={iconBtnClass}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="font-sans">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className={ui.tableHead}>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.challanId")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("dateTime.date")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.movementType")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.challanTo")}
              </th>
              {/* <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.stockJournal")}
              </th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.customerAllocation")}
              </th> */}
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.invoiceNumber")}
              </th>
              {/* <th className="px-4 py-3 font-semibold whitespace-nowrap text-left">
                {t("inventory.products")}
              </th> */}
              <th className="px-4 py-3 font-semibold whitespace-nowrap text-right">
                {t("common.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-text-border)/35">
            {pagedRows.map((row) => (
              <tr
                key={row.deliveryChallanId}
                className="hover:bg-workspace/20 transition-colors"
              >
                <td className="px-4 py-3.5 font-semibold text-foreground font-mono whitespace-nowrap">
                  {displayId(row.deliveryChallanId)}
                </td>
                <td className="px-4 py-3.5 per-file-hint whitespace-nowrap">
                  {formatDate(row.challanDate)}
                </td>
                <td className="px-4 py-3.5">
                  <MovementTypeBadge type={row.movementType} />
                </td>
                <td className="px-4 py-3.5 per-file-hint font-mono whitespace-nowrap">
                  {displayId(row.challanTo)}
                </td>
                {/* <td className="px-4 py-3.5 per-file-hint font-mono whitespace-nowrap">
                  {displayId(row.stockJournalId)}
                </td>
                <td className="px-4 py-3.5 per-file-hint font-mono whitespace-nowrap">
                  {displayId(row.customerAllocationId)}
                </td> */}
                <td className="px-4 py-3.5 per-file-hint font-mono whitespace-nowrap">
                  {row.invoiceNumber || "—"}
                </td>
                {/* <td className="px-4 py-3.5 per-file-hint max-w-[240px] truncate">
                  {productSummary(row.contentItems)}
                </td> */}
                <td className="px-4 py-3.5 text-right">
                  <ActionButtons row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="md:hidden space-y-2.5 p-3">
        {pagedRows.map((row) => (
          <li
            key={row.deliveryChallanId}
            className="rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {productSummary(row.contentItems)}
                </p>
                <p className="text-xs font-mono per-file-hint mt-0.5">
                  #{displayId(row.deliveryChallanId)}
                </p>
              </div>
              <ActionButtons row={row} />
            </div>
            <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25">
              <MetricRow
                label={t("dateTime.date")}
                value={formatDate(row.challanDate)}
              />
              <div className="flex justify-between gap-3 text-xs items-center">
                <span className="per-file-hint shrink-0">
                  {t("inventory.movementType")}
                </span>
                <MovementTypeBadge type={row.movementType} />
              </div>
              <MetricRow
                label={t("inventory.challanTo")}
                value={displayId(row.challanTo)}
              />
              <MetricRow
                label={t("inventory.stockJournal")}
                value={displayId(row.stockJournalId)}
              />
              <MetricRow
                label={t("inventory.customerAllocation")}
                value={displayId(row.customerAllocationId)}
              />
              <MetricRow
                label={t("inventory.invoiceNumber")}
                value={row.invoiceNumber || "—"}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="px-4 py-3">
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={resolvedTotalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
